import { Card } from '@/components/ui/card';
import { Divider } from '@/components/ui/divider';
import { HStack } from '@/components/ui/hstack';
import { Skeleton } from '@/components/ui/skeleton';
import { VStack } from '@/components/ui/vstack';
import { getAccessibilityProps } from '@/util/accessibilityProps';

/** Enough rows to fill a phone, and two or three rows of a desktop grid. */
const DEFAULT_ROW_COUNT = 3;

/**
 * One placeholder drawn to the geometry of `ExpertDirectoryCard`: the avatar and
 * name row, the divider, four description rows, then the tinted footer.
 *
 * The body runs four rows and the desktop footer is a single metrics bar, which lands the
 * card at the height a real one settles at, so the grid does not jump when data arrives. The
 * footer holds the action buttons on a phone, where the real card shows them, and the metrics
 * bar from `md` up, where it does not.
 *
 * Structurally empty and fixed-size on purpose: a placeholder that wraps real
 * content in a hidden style flashes on native.
 */
function DirectoryCardSkeleton() {
  return (
    <Card variant="outline" className="flex-1 overflow-hidden p-0 pt-4">
      <HStack className="items-center gap-6 px-4 web:gap-3">
        <Skeleton className="h-12 w-12" variant="circular" />

        <VStack className="flex-1 gap-2">
          <Skeleton className="h-5 w-40" />

          <Skeleton className="h-5 w-24" variant="rounded" />
        </VStack>
      </HStack>

      <Divider className="my-4" />

      <VStack className="mb-4 gap-3 px-4">
        <Skeleton className="h-4 w-full" />

        <Skeleton className="h-4 w-5/6" />

        <Skeleton className="h-4 w-4/6" />

        <Skeleton className="h-4 w-1/2" />
      </VStack>

      <VStack className="mt-4 border-t border-outline-100 bg-background-50 px-4 py-3">
        <HStack className="gap-4 md:hidden">
          <Skeleton className="h-8 flex-1" variant="rounded" />

          <Skeleton className="h-8 flex-1" variant="rounded" />
        </HStack>

        <Skeleton className="hidden h-5 w-32 md:flex" variant="rounded" />
      </VStack>
    </Card>
  );
}

type DirectoryCardSkeletonsProps = {
  /** Cards per row, matching the list's own column count. */
  numColumns?: number;
  rowCount?: number;
};

/**
 * Placeholder grid shown while the directory is fetching experts.
 *
 * Lays out its own rows, because a list's empty component sits outside the
 * list's columns. The root and each row refuse to shrink: the empty list's container is
 * `flex-1`, and without that the rows are squeezed to the viewport and the bars collapse. Carries `directory-loading` so a test can tell "still
 * loading" apart from "loaded and empty". Reads to a screen reader as one
 * loading indicator, with the bars inside hidden.
 */
export function DirectoryCardSkeletons({
  numColumns = 1,
  rowCount = DEFAULT_ROW_COUNT,
}: DirectoryCardSkeletonsProps) {
  return (
    <VStack
      testID="directory-loading"
      className="shrink-0 gap-3"
      {...getAccessibilityProps({ label: 'Loading experts', role: 'progressbar' })}>
      {[...Array(rowCount)].map((_, rowIndex) => (
        <HStack key={rowIndex} className="shrink-0 gap-3" aria-hidden>
          {[...Array(numColumns)].map((__, columnIndex) => (
            <DirectoryCardSkeleton key={columnIndex} />
          ))}
        </HStack>
      ))}
    </VStack>
  );
}
